import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronLeft, Edit2, CheckCircle } from 'lucide-react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import './CorporatePageElementsCss/CorporateBilling.css'
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  Box, Typography, Radio, RadioGroup, FormControlLabel, FormControl,
  FormLabel, TextField, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, InputAdornment, List, ListItem,
  ListItemButton, ListItemText, Paper, MenuItem, Select, InputLabel,
  Grid, Alert, AlertTitle, Divider, Chip, Stack, FormHelperText
} from '@mui/material';
import {
  Edit as EditIcon, Schedule, Warning, Science,
  Close as CloseIcon, Search as SearchIcon
} from '@mui/icons-material';
import { CreateBillwithSpecimen, getProfileStatus, getReferralDoctor, services } from '../../api/api';
import toast from 'react-hot-toast';

// Validation Schema
const validationSchema = Yup.object({
  mobileNo: Yup.string()
    .matches(/^[0-9]{10}$/, 'Mobile number must be exactly 10 digits')
    .required('Mobile number is required'),
  firstName: Yup.string()
    .min(2, 'First name must be at least 2 characters')
    .required('First name is required'),
  lastName: Yup.string()
    .min(2, 'Last name must be at least 2 characters')
    .required('Last name is required'),
  enterAge: Yup.number()
    .positive('Age must be positive')
    .integer('Age must be an integer')
    .min(1, 'Age must be at least 1')
    .max(120, 'Age must be less than 120'),
  dob: Yup.date()
    .max(new Date(), 'Date of birth cannot be in the future')
    .required('Date of birth is required'),
  email: Yup.string().email('Invalid email address'),
  altMobileNo: Yup.string()
    .matches(/^[0-9]{10}$/, 'Mobile number must be exactly 10 digits'),
  gender: Yup.string()
    .oneOf(['Male', 'Female'], 'Please select a gender')
    .required('Gender is required'),
  address: Yup.string()
    .min(10, 'Address must be at least 10 characters')
    .required('Address is required'),
  landmark: Yup.string(),
  cityName: Yup.string(),
  state: Yup.string(),
  pincode: Yup.string()
    .matches(/^[0-9]{6}$/, 'Pincode must be exactly 6 digits')
    .required('Pincode is required'),
  barcodeNumber: Yup.string()
});

export default function PatientFormFlow() {
  const [step, setStep] = useState(1);
  const [mobileNo, setMobile] = useState("");
  const [profileData, setProfileData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [patientDetails, setPatientDetails] = useState("");
  const [selectedPatient, setSelectedPatient] = useState("");
  const [servicesData, setServicesData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOption, setSelectedOption] = useState('');
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [newDoctorName, setNewDoctorName] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [tempSelectedDoctor, setTempSelectedDoctor] = useState('');
  const [filteredDoctors, setFilteredDoctors] = useState([]);
  const [selectedDoctorObject, setSelectedDoctorObject] = useState(null);

  const initialFormValues = {
     mobileNo: mobileNo,
    firstName: "",
    lastName: "",
    enterAge: "",
    dob: "",
    email: "",
    altMobileNo: "",
    gender: "",
    address: "",
    landmark: "",
    cityName: "",
    state: "",
    pincode: "",
    barcodeNumber: "",
    services_list: [],
    specimen_list: [],
    total_price: 0
  };

  const [formData, setFormData] = useState(initialFormValues);

  const handleOptionChange = (e) => {
    setSelectedOption(e.target.value);
    setNewDoctorName('');
    setSelectedDoctor('');
    setSelectedDoctorObject(null);
  };

  const handleSelectDoctor = () => {
    const doctorObj = filteredDoctors.find(doc => doc.DocName === tempSelectedDoctor);
    setSelectedDoctor(tempSelectedDoctor);
    setIsModalOpen(false);
    setSelectedDoctorObject(doctorObj);
    setSearchQuery('');
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setSearchQuery('');
    setTempSelectedDoctor('');
  };

  useEffect(() => {
    if (searchQuery.length >= 2) {
      fetchDoctors(searchQuery);
    } else {
      setFilteredDoctors([]);
    }
  }, [searchQuery]);

  const fetchDoctors = async () => {
    try {
      const payload = { name: searchQuery };
      setLoading(true);
      const response = await getReferralDoctor(payload);
      if (response?.data?.status === "success") {
        setFilteredDoctors(response.data.data || []);
      } else {
        setFilteredDoctors([]);
      }
    } catch (error) {
      console.error("Error fetching doctors:", error);
    } finally {
      setLoading(false);
    }
  };

  const HandleMobileChnage = (e, setFieldValue, setFieldTouched) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setMobile(value);
      setFieldValue('mobileNo', value);
      setFormData(prev => ({ ...prev, mobileNo: value })); 

      if (value.length === 10) {
        getProfileData(value);
      }
    }
  };

  const hasFastingTests = () => {
    return getSelectedTests().some(test => test.FastingReqd === "Y");
  };

  const getMaxReportTime = () => {
    const times = getSelectedTests().map(test => test.ReportTimeInDays || 0);
    return Math.max(...times, 0);
  };

  const formatDateToDDMMYYYY = (input) => {
    if (!input) return "";
    let date;
    if (typeof input === 'string' && input.includes('-')) {
      const parts = input.split('-');
      if (parts.length === 3) {
        if (parts[0].length === 2) return input;
        if (parts[0].length === 4) return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }
    date = new Date(input);
    if (isNaN(date)) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatDateToyyyymmdd = (dateString) => {
    if (!dateString) return '';
    const parts = dateString.split('-');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateString;
  };

  const getProfileData = async (mobileNumber) => {
    try {
      const payload = {
        RBAuthKey: "RBDWAh!Q1s74e",
        user_id: "",
        mobile_no: mobileNumber,
      };
      setLoading(true);
      const response = await getProfileStatus(payload);
      if (response?.data?.status === "success") {
        const profileData = response.data.data || [];
        setProfileData(profileData || {});
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const getFullName = (patient) => {
    const title = patient.PTitle ? `${patient.PTitle} ` : '';
    const firstName = patient.PFirstName?.trim() || '';
    const lastName = patient.PLastName?.trim() || '';
    return `${(`${title} ${firstName} ${lastName}`).trim() || 'Unnamed Patient'}`;
  };

  const handlePatientSelect = (e, setFieldValue) => {
    const userId = e.target.value;
    setSelectedPatient(userId);
    const selected = profileData.find(patient => patient.user_id === userId);
    if (selected) {
      setPatientDetails(selected);
    }
  };

useEffect(() => {
  if (patientDetails && typeof patientDetails === 'object') {
    const calculateAge = (dob) => {
      if (!dob) return "";
      try {
        const dateStr = dob.split('-').length === 3 && dob.split('-')[0].length === 2
          ? dob.split('-').reverse().join('-')
          : dob;
        const birthDate = new Date(dateStr);
        if (isNaN(birthDate)) return "";
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
        return age > 0 ? age.toString() : "";
      } catch {
        return "";
      }
    };

    const updatedData = {
      mobileNo: mobileNo,
      firstName: patientDetails.PFirstName || "",
      lastName: patientDetails.PLastName || "",
      email: patientDetails.email || "",
      gender: patientDetails.PSex === "M" ? "Male" : patientDetails.PSex === "F" ? "Female" : "",
      address: patientDetails.address || "",
      cityName: patientDetails.City || "",
      state: patientDetails.State || "",
      pincode: patientDetails.Zipcode || "",
      dob: patientDetails.dateofbirth || "",
      enterAge: calculateAge(patientDetails.dateofbirth),
      altMobileNo: patientDetails.alt_mobile_no || "",
      landmark: patientDetails.landmark || "",
      barcodeNumber: formData.barcodeNumber || "",
      services_list: formData.services_list,
      specimen_list: formData.specimen_list,
      total_price: formData.total_price
    };

    setFormData(updatedData);
  }
}, [patientDetails]);

  const handleSubmit = () => {
    CreateBillwithSpecimenCall();
  };
  console.log("patientDetails====>",patientDetails);
  

  const service = async () => {
    try {
      const payload = {
        RBAuthKey: "RBDWAh!Q1s74e",
        Center_ID: "711"
      };
      setLoading(true);
      const response = await services(payload);
      if (response?.data?.status === "success") {
        setServicesData(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching services:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    service();
  }, []);

  const handleTestToggle = (test) => {
    setFormData((prev) => {
      const isSelected = prev.services_list.includes(test.service_id);
      if (isSelected) {
        return {
          ...prev,
          services_list: prev.services_list.filter(id => id !== test.service_id),
          specimen_list: prev.specimen_list.filter((_, i) => prev.services_list[i] !== test.service_id),
          total_price: prev.total_price - test.price,
        };
      } else {
        return {
          ...prev,
          services_list: [...prev.services_list, test.service_id],
          specimen_list: [...prev.specimen_list, test.specimen],
          total_price: prev.total_price + test.price,
        };
      }
    });
  };

  const getSelectedTests = () => {
    return servicesData.filter(test =>
      formData.services_list.includes(test.service_id)
    );
  };

  useEffect(() => {
    const selectedTests = getSelectedTests();
    const specimenList = selectedTests.map(test =>
      test.specimenEntryReqd === "N" ? 0 : test.specimenEntryReqd
    );
    setFormData(prev => ({
      ...prev,
      specimen_list: specimenList
    }));
  }, [formData.services_list]);

  const CreateBillwithSpecimenCall = async () => {
    try {
      let refDoctor = "";
      let refDoctorID = "";
      if (selectedOption === "new") {
        refDoctor = newDoctorName;
        refDoctorID = "";
      } else if (selectedOption === "existing" && selectedDoctorObject) {
        refDoctor = "";
        refDoctorID = selectedDoctorObject.DoctorID.toString();
      }

      const payload = {
        RBAuthKey: "RBDWAh!Q1s74e",
        user_id: selectedPatient,
        Barcode: formData.barcodeNumber,
        BillDate: formatDateToDDMMYYYY(new Date()),
        PTitle: patientDetails.PTitle,
        FName: formData.firstName,
        LName: formData.lastName,
        dateofbirth: formatDateToyyyymmdd(formData.dob),
        Gender: formData.gender,
        mobile_no: mobileNo,
        email: formData.email,
        alt_mobile_no: formData.altMobileNo,
        landmark: formData.landmark,
        address: formData.address,
        city: formData.cityName,
        state: formData.state,
        zipCode: formData.pincode,
        ref_doctor: refDoctor,
        ref_doctorID: refDoctorID,
        services_list: Array.isArray(formData.services_list)
          ? formData.services_list.join(',')
          : (formData.services_list || ""),
        specimen_list: Array.isArray(formData.specimen_list)
          ? formData.specimen_list.join(',')
          : (formData.specimen_list || ""),
        total_price: formData.total_price,
        paidAmt: formData.total_price,
        paymode: "C",
        comments: "",
        ColCharge: "0",
        HDCharge: "0",
        phlebo_id: "0",
        center_ID: "",
        PromoCode: "",
        DiscPcn: "0",
        Disc_authBy: "0",
        Urgent: "N",
        Urgent_Time: "",
        MR_Remarks: "",
        CollChrgOptional: "y",
        HDReqd: "N",
        NotiFyOnWA: "Y",
        Appointment_ID: "",
        PrescImage: "",
        BarcodeOptional: "",
        COVIDSample: ""
      };
      console.log("final payload ==>",payload);
      

      setLoading(true);
      const response = await CreateBillwithSpecimen(payload);
      if (response?.data?.status === "success") {
        toast.success(response.data.Bill_ID);
      } else {
        toast.error(response?.data?.message || "error generating bill");
      }
    } catch (error) {
      toast.error("server error");
    } finally {
      setLoading(false);
    }
  };

  const steps = ["Details", "Tests", "Confirm"];
  console.log("mobileNo is ====>",mobileNo);
  console.log("searchTerm in test name ====>",searchTerm);
  console.log("test servicesData=================>",servicesData);

  
  
  

  return (
   <div className="min-h-screen !bg-[#30C1B1] py-8 px-4 custom-form-container">

      <div className="max-w-4xl mx-auto">
        {/* Progress Steps */}
        <Box mb={4}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            {steps.map((label, index) => {
              const s = index + 1;
              return (
                <React.Fragment key={s}>
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 600,
                        bgcolor: step >= s ? "primary.main" : "#fff",
                        color: step >= s ? "#fff" : "grey.500",
                        border: "2px solid",
                        borderColor: step >= s ? "primary.main" : "grey.300",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {step > s ? <CheckCircleIcon fontSize="small" /> : s}
                    </Box>
                    <Typography variant="caption" sx={{ mt: 1, fontWeight: 500, color: "text.secondary" }}>
                      {label}
                    </Typography>
                  </Box>
                  {s < steps.length && (
                    <Box
                      flex={1}
                      height={4}
                      mx={2}
                      sx={{
                        bgcolor: step > s ? "primary.main" : "grey.300",
                        borderRadius: 1,
                        transition: "all 0.3s ease",
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        </Box>

        {/* Form Card */}
        <div className="bg-white rounded-xl shadow-xl p-8">
          <Formik
            initialValues={formData}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched, validateForm }) => (
              <Form>
                {/* Step 1: Patient Details */}
                {step === 1 && (
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-6">Patient Details</h2>
                    <Grid container spacing={2}>
                      {/* Mobile No. & Select Patient */}
                      <Grid size={{ xs: 12, md: (profileData.length > 0 && mobileNo.length === 10) ? 4 : 12 }}>
                        <TextField
                          fullWidth
                          label="Mobile No."
                          name="mobileNo"
                          value={mobileNo}
                          onChange={(e) => HandleMobileChnage(e, setFieldValue, setFieldTouched)}
                          onBlur={handleBlur}
                          variant="outlined"
                          className='mobileNo'
                          size='small'
                          inputProps={{
                            maxLength: 10,
                            inputMode: 'numeric'
                          }}
                          error={touched.mobileNo && Boolean(errors.mobileNo)}
                          helperText={touched.mobileNo && errors.mobileNo}
                        />
                      </Grid>

                      {profileData.length > 0 && mobileNo.length === 10 && (
                        <Grid size={{ xs: 12, md: 8 }}>
                          <FormControl fullWidth>
                            <InputLabel id="patient-select-label">Select Patient</InputLabel>
                            <Select
                              labelId="patient-select-label"
                              id="patient-select"
                              value={selectedPatient}
                              label="Select Patient"
                              onChange={(e) => handlePatientSelect(e, setFieldValue)}
                              disabled={loading}
                              size='small'
                              className='patientInput'
                            >
                              {profileData.map((patient) => (
                                <MenuItem key={patient.user_id} value={patient.user_id}>
                                  {getFullName(patient)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      )}

                      {/* First Name, Last Name, Enter Age, DOB */}
                      <Grid size={{ xs: 12, md: 3 }}>
                        <TextField
                          fullWidth
                          required
                          label="First Name"
                          name="firstName"
                          value={values.firstName}
                          size='small'
                          onChange={(e) => {
                            handleChange(e);
                            setFormData(prev => ({ ...prev, firstName: e.target.value }));
                          }}
                          onBlur={handleBlur}
                          variant="outlined"
                          error={touched.firstName && Boolean(errors.firstName)}
                          helperText={touched.firstName && errors.firstName}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <TextField
                          fullWidth
                          required
                          label="Last Name"
                          name="lastName"
                          value={values.lastName}
                          onChange={(e) => {
                            handleChange(e);
                            setFormData(prev => ({ ...prev, lastName: e.target.value }));
                          }}
                          onBlur={handleBlur}
                          variant="outlined"
                          size='small'
                          error={touched.lastName && Boolean(errors.lastName)}
                          helperText={touched.lastName && errors.lastName}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <TextField
                          fullWidth
                          label="Enter Age"
                          name="enterAge"
                          type="number"
                          value={values.enterAge}
                          onChange={(e) => {
                            handleChange(e);
                            setFormData(prev => ({ ...prev, enterAge: e.target.value }));
                          }}
                          onBlur={handleBlur}
                          variant="outlined"
                          size='small'
                          error={touched.enterAge && Boolean(errors.enterAge)}
                          helperText={touched.enterAge && errors.enterAge}
                        />
                      </Grid>
                    {/*  <Grid size={{ xs: 12, md: 3 }}>
                        <TextField
                          fullWidth
                          required
                          label="D.O.B"
                          name="dob"
                          type="date"
                          value={formatDateToyyyymmdd(values.dob)}
                          onChange={(e) => {
                            handleChange(e);
                            setFormData(prev => ({ ...prev, dob: e.target.value }));
                          }}
                          onBlur={handleBlur}
                          variant="outlined"
                          size='small'
                          InputLabelProps={{ shrink: true }}
                          error={touched.dob && Boolean(errors.dob)}
                          helperText={touched.dob && errors.dob}
                        />
                      </Grid>*/}
<Grid size={{ xs: 12, md: 3 }}>
<TextField
  fullWidth
  required
  label="D.O.B"
  name="dob"
  type="date"
  value={values.dob || ""}
  onChange={(e) => {
    handleChange(e);
    setFormData(prev => ({ ...prev, dob: e.target.value }));
  }}
  onBlur={handleBlur}
  variant="outlined"
  size='small'
  InputLabelProps={{ shrink: true }}
  error={touched.dob && Boolean(errors.dob)}
  helperText={touched.dob && errors.dob}
/>
  </Grid>

 {/* Email, Alt Mobile No, Gender */}
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="Email"
                          name="email"
                          type="email"
                          value={values.email}
                          onChange={(e) => {
                            handleChange(e);
                            setFormData(prev => ({ ...prev, email: e.target.value }));
                          }}
                          onBlur={handleBlur}
                          variant="outlined"
                          size='small'
                          error={touched.email && Boolean(errors.email)}
                          helperText={touched.email && errors.email}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="Alt Mobile No."
                          name="altMobileNo"
                          value={values.altMobileNo}
                          onChange={(e) => {
                            handleChange(e);
                            setFormData(prev => ({ ...prev, altMobileNo: e.target.value }));
                          }}
                          onBlur={handleBlur}
                          variant="outlined"
                          size='small'
                          inputProps={{
                            maxLength: 10,
                            inputMode: 'numeric'
                          }}
                          error={touched.altMobileNo && Boolean(errors.altMobileNo)}
                          helperText={touched.altMobileNo && errors.altMobileNo}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <FormControl component="fieldset" error={touched.gender && Boolean(errors.gender)}>
                          <FormLabel required sx={{ mb: 1 }}>Gender</FormLabel>
                          <RadioGroup
                            row
                            name="gender"
                            value={values.gender}
                            onChange={(e) => {
                              handleChange(e);
                              setFormData(prev => ({ ...prev, gender: e.target.value }));
                            }}
                            onBlur={handleBlur}
                            size='small'
                          >
                            <FormControlLabel value="Male" control={<Radio />} label="Male" />
                            <FormControlLabel value="Female" control={<Radio />} label="Female" />
                          </RadioGroup>
                          {touched.gender && errors.gender && (
                            <FormHelperText>{errors.gender}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>

                      {/* Address, Landmark */}
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          required
                          label="Address"
                          name="address"
                          size='small'
                          multiline
                          rows={1}
                          value={values.address}
                          onChange={(e) => {
                            handleChange(e);
                            setFormData(prev => ({ ...prev, address: e.target.value }));
                          }}
                          onBlur={handleBlur}
                          variant="outlined"
                          error={touched.address && Boolean(errors.address)}
                          helperText={touched.address && errors.address}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          label="Landmark"
                          name="landmark"
                          size='small'
                          multiline
                          rows={1}
                          value={values.landmark}
                          onChange={(e) => {
                            handleChange(e);
                            setFormData(prev => ({ ...prev, landmark: e.target.value }));
                          }}
                          onBlur={handleBlur}
                          variant="outlined"
                        />
                      </Grid>

                      {/* City Name, State, Pincode */}
                      <Grid size={{ xs: 12, md: 4 }}>
                        <FormControl fullWidth>
                          <InputLabel>City Name</InputLabel>
                          <Select
                            name="cityName"
                            value={values.cityName}
                            onChange={(e) => {
                              handleChange(e);
                              setFormData(prev => ({ ...prev, cityName: e.target.value }));
                            }}
                            onBlur={handleBlur}
                            label="City Name"
                            size='small'
                          >
                            <MenuItem value="Kolkata">Kolkata</MenuItem>
                            <MenuItem value="Mumbai">Mumbai</MenuItem>
                            <MenuItem value="Delhi">Delhi</MenuItem>
                            <MenuItem value="Bangalore">Bangalore</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="State"
                          name="state"
                          value={values.state}
                          onChange={(e) => {
                            handleChange(e);
                            setFormData(prev => ({ ...prev, state: e.target.value }));
                          }}
                          onBlur={handleBlur}
                          size='small'
                          variant="outlined"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          required
                          label="Pincode"
                          name="pincode"
                          value={values.pincode}
                          onChange={(e) => {
                            handleChange(e);
                            setFormData(prev => ({ ...prev, pincode: e.target.value }));
                          }}
                          onBlur={handleBlur}
                          size='small'
                          variant="outlined"
                          inputProps={{
                            maxLength: 6,
                            inputMode: 'numeric'
                          }}
                          error={touched.pincode && Boolean(errors.pincode)}
                          helperText={touched.pincode && errors.pincode}
                        />
                      </Grid>

                      {/* Barcode Number */}
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          label="Barcode Number"
                          name="barcodeNumber"
                          value={values.barcodeNumber}
                          onChange={(e) => {
                            handleChange(e);
                            setFormData(prev => ({ ...prev, barcodeNumber: e.target.value }));
                          }}
                          onBlur={handleBlur}
                          variant="outlined"
                          size='small'
                        />
                      </Grid>
                    </Grid>

                    <div className="flex justify-end mt-8">
                      <button
                        type="button"
                        onClick={async () => {
                          const validationErrors = await validateForm();
                          if (Object.keys(validationErrors).length === 0) {
                            setStep(2);
                          } else {
                            // Mark all fields as touched to show errors
                            Object.keys(values).forEach(key => {
                              setFieldTouched(key, true);
                            });
                            toast.error("Please fill all required fields correctly");
                          }
                        }}
                        className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors flex items-center gap-2 next-button"
                      >
                        Next: Select Tests
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 2: Test Selection */}
                {step === 2 && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Typography variant="h5" fontWeight="bold">
                        Test Details
                      </Typography>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => setStep(1)}
                        sx={{ minWidth: 'auto', color: 'text.secondary' }}
                      >
                        ✕
                      </Button>
                    </Box>

                    {/* Search Box */}
                    <TextField
                      fullWidth
                      placeholder="Search by Name"
                      variant="outlined"
                      size="small"
                      onChange={(e) => setSearchTerm(e.target.value)}
                      sx={{ mb: 3 }}
                      InputProps={{
                        endAdornment: (
                          <Box component="span" sx={{ color: 'text.secondary' }}>
                            🔍
                          </Box>
                        )
                      }}
                    />

                    {/* Test Table */}
                    <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                      {/* Table Header */}
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: '50px 1fr 120px',
                        bgcolor: '#30C1B1',
                        color: 'white',
                        p: 2,
                        fontWeight: 600,
                        position: 'sticky',
                        top: 0,
                        zIndex: 1
                      }}>
                        <Box></Box>
                        <Box>Test Name</Box>
                        <Box sx={{ textAlign: 'right' }}>Price</Box>
                      </Box>

                      {/* Table Body - Scrollable */}
                      <Box sx={{
                        maxHeight: '250px',
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': {
                          width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          bgcolor: 'grey.100',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          bgcolor: 'grey.400',
                          borderRadius: '4px',
                          '&:hover': {
                            bgcolor: 'grey.500',
                          }
                        }
                      }}>
                        {servicesData
                        .filter(test => 
                            test.service_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            test.service_key?.toLowerCase().includes(searchTerm.toLowerCase())
                          )
                        .map((test, index) => (
                          <Box
                            key={test.service_id}
                            onClick={() => handleTestToggle(test)}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: '50px 1fr 120px',
                              p: 2,
                              bgcolor: index % 2 === 0 ? 'grey.50' : 'white',
                              cursor: 'pointer',
                              borderBottom: 1,
                              borderColor: 'divider',
                              '&:hover': {
                                bgcolor: 'action.hover'
                              },
                              transition: 'all 0.2s'
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <input
                                type="checkbox"
                                checked={formData?.services_list?.includes(test.service_id)}
                                onChange={() => { }}
                                style={{
                                  width: 18,
                                  height: 18,
                                  cursor: 'pointer',
                                  accentColor: '#30C1B1'
                                }}
                              />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body2" fontWeight="500">
                                {test.service_name}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                              <Typography variant="body2" fontWeight="500">
                                {test.price}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Paper>

                    {/* Total Section */}
                    <Paper
                      elevation={0}
                      sx={{
                        mt: 3,
                        p: 3,
                        bgcolor: 'primary.50',
                        border: 1,
                        borderColor: 'primary.200',
                        borderRadius: 2
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="h6" fontWeight="600">
                          Total Amount:
                        </Typography>
                        <Typography variant="h4" color="primary" fontWeight="bold">
                          ₹{formData.total_price}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {formData.services_list.length} test(s) selected
                      </Typography>
                    </Paper>

                    {/* Select Ref Doc Portion */}
                    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
                      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                        Select Referral Doctor
                      </Typography>

                      {/* Radio Button Selection */}
                      <FormControl component="fieldset" sx={{ mb: 3 }}>
                        <FormLabel component="legend">Select Referral Doctor Type</FormLabel>
                        <RadioGroup
                          name="refDoctorType"
                          value={selectedOption}
                          onChange={handleOptionChange}
                        >
                          <FormControlLabel
                            value="new"
                            control={<Radio />}
                            label="New"
                          />
                          <FormControlLabel
                            value="existing"
                            control={<Radio />}
                            label="Existing"
                          />
                        </RadioGroup>
                      </FormControl>

                      {selectedOption === "new" && (
                        <TextField
                          fullWidth
                          label="Enter Doctor Name"
                          variant="outlined"
                          size="small"
                          value={newDoctorName}
                          onChange={(e) => setNewDoctorName(e.target.value)}
                        />
                      )}

                      {selectedOption === 'existing' && (
                        <Box>
                          {selectedDoctor && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                              <Typography variant="body2">
                                <strong>Selected Doctor:</strong> {selectedDoctor}
                              </Typography>
                            </Alert>
                          )}

                          <Button
                            variant="contained"
                            onClick={() => setIsModalOpen(true)}
                            sx={{
                              bgcolor: '#30C1B1',
                              '&:hover': {
                                bgcolor: '#30C1B1'
                              },
                              textTransform: 'none',
                              px: 3,
                              py: 1
                            }}
                          >
                            Choose Referral Doctor
                          </Button>
                        </Box>
                      )}

                      {/* Modal Dialog */}
                      <Dialog
                        open={isModalOpen}
                        onClose={handleCancel}
                        maxWidth="md"
                        fullWidth
                        PaperProps={{
                          sx: {
                            borderRadius: 2,
                            maxHeight: '80vh'
                          }
                        }}
                      >
                        {/* Dialog Header */}
                        <DialogTitle
                          sx={{
                            bgcolor: 'success.main',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            py: 2
                          }}
                        >
                          <Typography variant="h6" component="div">
                            Referral Doctor List
                          </Typography>
                          <IconButton
                            onClick={handleCancel}
                            sx={{
                              color: 'white',
                              '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.1)'
                              }
                            }}
                          >
                            <CloseIcon />
                          </IconButton>
                        </DialogTitle>

                        {/* Dialog Content */}
                        <DialogContent sx={{ p: 3 }} className='search-and-list'>
                          {/* Search Field */}
                          <TextField
                            fullWidth
                            placeholder="Search by Name ( Enter atleast 2 characters, don't add 'Dr.' while typing )"
                            variant="outlined"
                            className='searchNameField'
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <SearchIcon color="action" />
                                </InputAdornment>
                              )
                            }}
                            sx={{ mb: 3 }}
                          />

                          {/* Doctor List */}
                          <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
                            {searchQuery.length >= 2 ? (
                              filteredDoctors.length > 0 ? (
                                <Box>
                                  {/* Table Header */}
                                  <Box
                                    sx={{
                                      display: 'grid',
                                      gridTemplateColumns: '50px 2fr 1.5fr 1.5fr',
                                      bgcolor: '#30C1B1',
                                      color: 'white',
                                      p: 2,
                                      fontWeight: 600,
                                      position: 'sticky',
                                      top: 0,
                                      zIndex: 1,
                                      borderBottom: '2px solid #fff'
                                    }}
                                  >
                                    <Box></Box>
                                    <Box>Doctor Name</Box>
                                    <Box>Reg. No.</Box>
                                    <Box>Speciality</Box>
                                  </Box>

                                  {/* Table Body */}
                                  <RadioGroup
                                    value={tempSelectedDoctor}
                                    onChange={(e) => setTempSelectedDoctor(e.target.value)}
                                  >
                                    <List disablePadding>
                                      {filteredDoctors.map((doctor, index) => (
                                        <ListItem
                                          key={`${doctor.DoctorID}-${index}`}
                                          disablePadding
                                          sx={{
                                            borderBottom: index < filteredDoctors.length - 1 ? '1px solid #e0e0e0' : 'none',
                                            '&:hover': {
                                              bgcolor: 'rgba(76, 175, 80, 0.08)'
                                            }
                                          }}
                                        >
                                          <ListItemButton
                                            sx={{
                                              display: 'grid',
                                              gridTemplateColumns: '50px 2fr 1.5fr 1.5fr',
                                              alignItems: 'center',
                                              py: 1.5,
                                              px: 2
                                            }}
                                          >
                                            <Box>
                                              <Radio
                                                color="success"
                                                value={doctor.DocName}
                                                checked={tempSelectedDoctor === doctor.DocName}
                                              />
                                            </Box>
                                            <Box>
                                              <Typography variant="body2" fontWeight="500">
                                                Dr. {doctor.DocName}
                                              </Typography>
                                            </Box>
                                            <Box>
                                              <Typography variant="body2" color="text.secondary">
                                                {doctor.RegNo}
                                              </Typography>
                                            </Box>
                                            <Box>
                                              <Typography variant="body2" color="text.secondary">
                                                {doctor.specialization}
                                              </Typography>
                                            </Box>
                                          </ListItemButton>
                                        </ListItem>
                                      ))}
                                    </List>
                                  </RadioGroup>
                                </Box>
                              ) : (
                                <Box sx={{ p: 4, textAlign: 'center' }}>
                                  <Typography color="text.secondary">
                                    No doctors found matching your search
                                  </Typography>
                                </Box>
                              )
                            ) : (
                              <Box sx={{ p: 4, textAlign: 'center' }}>
                                <Typography color="text.secondary">
                                  Please enter at least 2 characters to search
                                </Typography>
                              </Box>
                            )}
                          </Paper>
                        </DialogContent>

                        {/* Dialog Actions */}
                        <DialogActions sx={{ p: 3, pt: 0 }}>
                          <Button
                            onClick={handleCancel}
                            variant="contained"
                            sx={{
                              bgcolor: '#757575',
                              '&:hover': {
                                bgcolor: '#616161'
                              },
                              textTransform: 'none',
                              px: 3
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={handleSelectDoctor}
                            disabled={!tempSelectedDoctor}
                            variant="contained"
                            color="success"
                            sx={{
                              textTransform: 'none',
                              px: 3
                            }}
                          >
                            Select
                          </Button>
                        </DialogActions>
                      </Dialog>
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 4 }}>
                      <Button
                        variant="outlined"
                        size="large"
                        startIcon={<ChevronLeft />}
                        onClick={() => setStep(1)}
                        sx={{ textTransform: 'none', px: 4 }}
                      >
                        Back
                      </Button>
                      <Button
                        variant="contained"
                        size="large"
                        endIcon={<ChevronRight />}
                        onClick={() => setStep(3)}
                        disabled={formData.services_list.length === 0}
                        sx={{
                          textTransform: 'none',
                          px: 4,
                          bgcolor: '#30C1B1',
                          '&:hover': {
                            bgcolor: '#30C1B1'
                          }
                        }}
                      >
                        Review & Confirm
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Step 3: Confirmation */}
                {step === 3 && (
                  <Box>
                    <Typography variant="h4" fontWeight="bold" gutterBottom>
                      Review & Confirm
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                      Please review your booking details before confirming
                    </Typography>

                    {/* Important Alerts */}
                    <Stack spacing={2} sx={{ mb: 3 }}>
                      {hasFastingTests() && (
                        <Alert severity="warning" icon={<Warning />}>
                          <AlertTitle sx={{ fontWeight: 'bold' }}>Fasting Required</AlertTitle>
                          Some of your selected tests require fasting. Please check preparation instructions below.
                        </Alert>
                      )}

                      <Alert severity="info" icon={<Schedule />}>
                        <AlertTitle sx={{ fontWeight: 'bold' }}>Report Timeline</AlertTitle>
                        {getMaxReportTime() === 0
                          ? "Your reports will be available on the same day"
                          : `Your reports will be available within ${getMaxReportTime()} ${getMaxReportTime() === 1 ? 'day' : 'days'}`
                        }
                      </Alert>
                    </Stack>

                    {/* Patient Details Section */}
                    <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', mb: 3, borderRadius: 2 }}>
                      <Box sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                          <Typography variant="h6" fontWeight="600">
                            Patient Details
                          </Typography>
                          <Button
                            startIcon={<EditIcon />}
                            onClick={() => setStep(1)}
                            size="small"
                            sx={{ textTransform: 'none' }}
                          >
                            Edit
                          </Button>
                        </Box>

                        <Grid container spacing={3}>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                              Full Name
                            </Typography>
                            <Typography variant="body1" fontWeight="500">
                              {formData.firstName} {formData.lastName}
                            </Typography>
                          </Grid>

                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                              Date of Birth
                            </Typography>
                            <Typography variant="body1" fontWeight="500">
                              {formatDateToDDMMYYYY(formData.dob)}
                            </Typography>
                          </Grid>

                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                              Gender
                            </Typography>
                            <Typography variant="body1" fontWeight="500">
                              {formData.gender}
                            </Typography>
                          </Grid>

                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                              Mobile Number
                            </Typography>
                            <Typography variant="body1" fontWeight="500">
                              {mobileNo}
                            </Typography>
                          </Grid>

                          <Grid size={{ xs: 12 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                              Address
                            </Typography>
                            <Typography variant="body1" fontWeight="500">
                              {formData.address}, {formData.landmark && `${formData.landmark}, `}
                              {formData.cityName}, {formData.state} - {formData.pincode}
                            </Typography>
                          </Grid>

                          {formData.email && (
                            <Grid size={{ xs: 12 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Email
                              </Typography>
                              <Typography variant="body1" fontWeight="500">
                                {formData.email}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </Paper>

                    {/* Selected Tests Section */}
                      {/* Selected Tests Section */}
                    <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
                      <Box sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                          <Typography variant="h6" fontWeight="600">
                            Selected Tests
                          </Typography>
                          <Button
                            startIcon={<EditIcon />}
                            onClick={() => setStep(2)}
                            size="small"
                            sx={{ textTransform: 'none' }}
                          >
                            Edit
                          </Button>
                        </Box>

                        <Box sx={{
                          maxHeight: '400px',
                          overflowY: 'auto',
                          pr: 1,
                          '&::-webkit-scrollbar': {
                            width: '8px',
                          },
                          '&::-webkit-scrollbar-track': {
                            bgcolor: 'grey.100',
                            borderRadius: '4px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            bgcolor: 'grey.400',
                            borderRadius: '4px',
                            '&:hover': {
                              bgcolor: 'grey.500',
                            }
                          }
                        }}>
                          <Stack spacing={2}>
                            {getSelectedTests().map(test => (
                            <Paper
                              key={test.service_id}
                              elevation={0}
                              sx={{
                                bgcolor: 'grey.50',
                                p: 2.5,
                                border: 1,
                                borderColor: 'grey.200',
                                borderRadius: 2
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="h6" fontWeight="600" gutterBottom>
                                    {test.service_name || test.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {test.service_key || `Test ID: ${test.service_id || test.id}`}
                                  </Typography>
                                </Box>
                                <Typography variant="h5" color="primary" fontWeight="bold" sx={{ ml: 2 }}>
                                  ₹{test.price || 0}
                                </Typography>
                              </Box>

                              {test.service_info && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                  {test.service_info}
                                </Typography>
                              )}

                              {/* Preparation Instructions */}
                              {test.service_preperation && (
                                <Alert
                                  severity={test.FastingReqd === "Y" ? "warning" : "info"}
                                  sx={{ mb: 2 }}
                                  icon={<Warning />}
                                >
                                  <Typography variant="body2">
                                    <strong>Preparation: </strong>
                                    {test.service_preperation}
                                  </Typography>
                                </Alert>
                              )}

                              {/* Sample & Report Info */}
                              <Stack direction="row" spacing={2} flexWrap="wrap">
                                {test.SampleName && (
                                  <Chip
                                    icon={<Science />}
                                    label={`Sample: ${test.SampleName}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                                <Chip
                                  icon={<Schedule />}
                                  label={`Report: ${test.ReportTimeInDays === 0
                                    ? "Same day"
                                    : `${test.ReportTimeInDays} ${test.ReportTimeInDays === 1 ? 'day' : 'days'}`}`}
                                  size="small"
                                  variant="outlined"
                                />
                              </Stack>
                            </Paper>
                          ))}
                          </Stack>
                        </Box>

                        {/* Total */}
                        <Divider sx={{ my: 3 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="h6" fontWeight="bold">
                            Total Amount:
                          </Typography>
                          <Typography variant="h4" color="primary" fontWeight="bold">
                            ₹{formData.total_price}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 4, flexDirection: { xs: 'column', sm: 'row' } }}>
                      <Button
                        variant="outlined"
                        size="large"
                        startIcon={<ChevronLeft />}
                        onClick={() => setStep(2)}
                        sx={{ textTransform: 'none', px: 4 }}
                      >
                        Back
                      </Button>
                      <Button
                        variant="contained"
                        size="large"
                        className='confirm-Button'
                        color="success"
                        startIcon={<CheckCircle />}
                        onClick={handleSubmit}
                        sx={{ textTransform: 'none', px: 4 }}
                      >
                        Confirm Booking
                      </Button>
                    </Box>
                  </Box>
                )}
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
}